import { createFileRoute } from "@tanstack/react-router"
import { SambaTheme } from "ipctg-inflow-types/InFlowThemes"
import { PropsWithChildren } from "react"
import { Trans, useTranslation } from "react-i18next"

import { IconName } from "@/assets/icons"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import {
  DEFAULT_HELP_EMAIL,
  DEFAULT_HELP_PHONE,
  DEFAULT_ONSITE_HELP_TITLE,
} from "@/utilities/globals"
import { formatPhoneNumberForDisplay } from "@/utilities/phone"

export const Route = createFileRoute("/help")({
  staticData: {
    pageName: "Help",
  },
  loader: ({ context }) => {
    context.setDocumentThemeMode("root")
  },
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <HelpRouteComponent
      hasOnsiteHelp={false}
      phone={DEFAULT_HELP_PHONE}
      email={DEFAULT_HELP_EMAIL}
      onsiteHelperTitle=""
    />
  )
}

type HelpProps = SambaTheme["locationInformation"]["support"]

export function HelpRouteComponent(props: HelpProps) {
  const { t } = useTranslation()

  const phone = formatPhoneNumberForDisplay(props.phone)
  const mailTo = `mailto:${props.email}`
  const tel = `tel:${props.phone}`
  const onsiteHelpTitle = props.onsiteHelperTitle || DEFAULT_ONSITE_HELP_TITLE

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("help.header")}
      </Heading>
      {props.hasOnsiteHelp && (
        <HelpCard supportType="onsite">
          <Trans
            i18nKey="help.onsiteDescription"
            values={{
              onsiteHelpTitle,
            }}
          >
            Seek out a <span>{onsiteHelpTitle}</span> for immediate assistance.
          </Trans>
        </HelpCard>
      )}
      {props.email && (
        <HelpCard supportType="email">
          <Trans i18nKey="help.email" values={{ email: props.email }}>
            You can email us at <a href={mailTo}>{props.email}</a>
          </Trans>
        </HelpCard>
      )}
      {props.phone && (
        <HelpCard supportType="phone">
          <Trans
            i18nKey="help.phone"
            values={{
              phone,
            }}
          >
            You can call us at <a href={tel}>{phone}</a>
          </Trans>
        </HelpCard>
      )}
    </>
  )
}

interface HelpCellProps {
  supportType: "onsite" | "phone" | "email"
}

function HelpCard(props: PropsWithChildren<HelpCellProps>) {
  const { t } = useTranslation()
  return (
    <Card>
      <address className="help-card">
        <div className="help-card--icon">
          <Icon size="default" name={iconFor()} />
        </div>
        <Heading level={3} as={2} style={{ marginTop: "var(--space-5)" }}>
          {titleFor()}
        </Heading>
        <Text className="medium" style={{ marginTop: "var(--space-1)" }}>
          {props.children}
        </Text>
      </address>
    </Card>
  )

  function iconFor(): IconName {
    switch (props.supportType) {
      case "onsite":
        return "person"
      case "phone":
        return "phone"
      case "email":
        return "mail"
    }
  }

  function titleFor() {
    switch (props.supportType) {
      case "onsite":
        return t("help.onsiteSubhead")
      case "phone":
        return t("help.phoneSubhead")
      case "email":
        return t("help.emailSubhead")
    }
  }
}
